// App.tsx (or App.js if you remove TypeScript)
import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  AppBar,
  Link,
  Paper,
  alpha,
  styled,
  keyframes,
  GlobalStyles,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import home from '../assets/me_logo.png'
import tech_home from '../assets/teacher.png'
import ved_thumb from '../assets/video_thumb.png'
import finance from '../assets/finance.png'
import laptop from '../assets/laptop.png'

const theme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#022c47',
      // default: '#05070a',
      paper: 'rgba(18, 22, 35, 0.6)',
    },
    primary: { main: '#6366f1' },
    secondary: { main: '#a5b4fc' },
    text: {
      primary: '#eef2ff',
      secondary: '#b9c3d4',
    },
  },
  typography: {
    fontFamily: "'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
    h2: { fontWeight: 800, letterSpacing: '-0.02em' },
    h3: { fontWeight: 700, letterSpacing: '-0.02em' },
  },
  shape: { borderRadius: 32 },
});

// --- Animations ---
const fadeScale = keyframes`
  0% { opacity: 0; transform: scale(0.97); }
  100% { opacity: 1; transform: scale(1); }
`;

// --- Styled Components (mirror original CSS) ---

// const StyledAppBar = styled(AppBar)(({ theme }) => ({
//   position: 'sticky',
//   top: 0,
//   zIndex: 50,
//   backdropFilter: 'blur(16px)',
//   backgroundColor: alpha(theme.palette.background.default, 0.75),
//   borderBottom: `1px solid ${alpha('#fff', 0.08)}`,
//   boxShadow: 'none',
// }));

// const GradientLogo = styled(Typography)({
//   background: 'linear-gradient(135deg, #FFFFFF 0%, #9CA3FF 100%)',
//   WebkitBackgroundClip: 'text',
//   backgroundClip: 'text',
//   color: 'transparent',
//   fontWeight: 700,
//   letterSpacing: '-0.02em',
//   fontSize: '1.8rem',
// });

// const HeroGradient = styled(Typography)({
//   background: 'linear-gradient(to right, #fff, #c4b5fd, #94a3f8)',
//   WebkitBackgroundClip: 'text',
//   backgroundClip: 'text',
//   color: 'transparent',
//   fontWeight: 800,
//   fontSize: { xs: '2.5rem', md: '3.5rem' },
//   lineHeight: 1.2,
// });

const FilterChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ theme, active }) => ({
  backgroundColor: active
    ? theme.palette.primary.main
    : alpha('#fff', 0.08),
  color: active ? '#fff' : '#cbd5e6',
  border: active
    ? `1px solid ${theme.palette.secondary.main}`
    : '1px solid rgba(255,255,255,0.08)',
  fontWeight: 500,
  fontSize: '0.85rem',
  padding: '8px 24px',
  height: 'auto',
  borderRadius: 60,
  transition: 'all 0.2s',
  '&:hover': {
    backgroundColor: active
      ? theme.palette.primary.main
      : alpha(theme.palette.primary.main, 0.3),
    color: '#fff',
  },
}));

const PremiumCard = styled(Card)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.background.paper, 0.6),
  backdropFilter: 'blur(2px)',
  borderRadius: 10,
  border: `1px solid ${alpha('#fff', 0.06)}`,
  transition: 'all 0.4s cubic-bezier(0.2, 0.9, 0.4, 1.1)',
  boxShadow: '0 20px 35px -15px rgba(0,0,0,0.5)',
  overflow: 'hidden',
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  animation: `${fadeScale} 0.35s ease forwards`,
  '&:hover': {
    transform: 'translateY(-8px)',
    borderColor: alpha(theme.palette.primary.main, 0.4),
    boxShadow: '0 30px 45px -20px rgba(0,0,0,0.7), 0 0 0 1px rgba(99, 102, 241, 0.2)',
    backgroundColor: alpha('#161b2b', 0.8),
  },
}));

const MediaWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  aspectRatio: '2 / 5',   // default (mobile)
  overflow: 'hidden',
  backgroundColor: '#07090f',

  [theme.breakpoints.up('md')]: {
    aspectRatio: '4 / 3',   // desktop
  },

  '& img, & video': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.6s ease',
    display: 'block',
  },

  '&:hover img, &:hover video': {
    transform: 'scale(1.03)',
  },
}));

const FeaturedWrapper = styled(Paper)(({ theme }) => ({
  background: 'linear-gradient(145deg, #0b0e17, #07090f)',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.05)}`,
  borderRadius: 10,
  overflow: 'hidden',
  margin: '40px 0 60px',
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
}));

const NewsletterBox = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(115deg, #0f1222, #080b12)',
  borderRadius: '2rem',
  padding: '48px 32px',
  textAlign: 'center',
  border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
  margin: '40px 0 64px',
}));

// --- Data ---
const postsData = [
  {
    id: 1,
    type: 'news',
    title: 'Future‑ready classrooms: AI in school management',
    excerpt: 'How artificial intelligence is transforming administrative tasks, personalised learning, and student assessment in modern schools.',
    metaCategory: '📊  EdTech',
    mediaType: 'image',
    mediaSrc: `${laptop}`,
    readTime: '8 min read · innovation',
    linkText: 'read more →',
  },
  {
    id: 2,
    type: 'video',
    title: 'A day in the life of a principal',
    excerpt: 'Behind the scenes of effective school leadership: balancing curriculum, staff morale, and community engagement.',
    metaCategory: '🎥  leadership',
    mediaType: 'video',
    mediaSrc: 'https://interactive-examples.mdn.mozilla.net/media/cc0-videos/earth.mp4',
    posterSrc: 'https://images.pexels.com/photos/2386276/pexels-photo-2386276.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    duration: '7:22 min · documentary',
    linkText: 'watch trailer →',
  },
  {
    id: 3,
    type: 'news',
    title: 'Teacher well‑being matters',
    excerpt: 'Strategies to reduce burnout and foster a supportive school culture. Insights from leading educational psychologists.',
    metaCategory: '🧠  wellness',
    mediaType: 'image',
    mediaSrc: `${tech_home}`,
    readTime: '10 min read · feature',
    linkText: 'explore →',
  },
  {
    id: 4,
    type: 'news',
    title: 'Redefining parent‑teacher communication',
    excerpt: 'How digital platforms and regular feedback loops are strengthening school‑home partnerships.',
    metaCategory: '📱  engagement',
    mediaType: 'image',
    mediaSrc: 'https://images.pexels.com/photos/6646918/pexels-photo-6646918.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    readTime: '6 min read · trends',
    linkText: 'read dispatch →',
  },
  {
    id: 5,
    type: 'video',
    title: 'Student‑led conferences: empowering young voices',
    excerpt: 'A short film showcasing how one school transformed parent meetings into student‑driven showcases of learning.',
    metaCategory: '🎬  student agency',
    mediaType: 'video',
    mediaSrc: 'https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4',
    posterSrc: `${ved_thumb}`,
    duration: '4:15 min · case study',
    linkText: 'watch full video →',
  },
  {
    id: 6,
    type: 'image',
    title: 'Green schools: sustainability in education',
    excerpt: 'From solar panels to eco‑curriculum, how schools are leading the charge on environmental responsibility.',
    metaCategory: '🌱  sustainability',
    mediaType: 'image',
    mediaSrc: 'https://images.pexels.com/photos/1769490/pexels-photo-1769490.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    readTime: '5 min read · special report',
    linkText: 'learn more →',
  },
  {
    id: 7,
    type: 'news',
    title: 'Data‑driven decision making for school leaders',
    excerpt: 'How analytics can improve attendance, academic outcomes, and resource allocation without losing the human touch.',
    metaCategory: '📈  analytics',
    mediaType: 'image',
    mediaSrc: `${finance}`,
    readTime: '9 min read · guide',
    linkText: 'read story →',
  },
  {
    id: 8,
    type: 'video',
    title: 'Inclusive education in action',
    excerpt: 'A documentary on building classrooms where every child, regardless of ability, thrives.',
    metaCategory: '🤝  inclusion',
    mediaType: 'video',
    mediaSrc: 'https://interactive-examples.mdn.mozilla.net/media/cc0-videos/earth.mp4',
    posterSrc: 'https://images.pexels.com/photos/8138520/pexels-photo-8138520.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
    duration: '11:30 min · documentary',
    linkText: 'watch excerpt →',
  },
];

const filterOptions = [
  { label: 'All stories', value: 'all' },
  { label: 'Activities', value: 'image' },
  { label: 'tutorial Video', value: 'video' },
  { label: 'News & culture', value: 'news' },
];

// --- Helper for badge colors ---
const getMetaBadgeColor = (type) => {
  switch (type) {
    case 'image': return '#60a5fa';
    case 'video': return '#4ade80';
    case 'news': return '#fb923c';
    default: return '#a5b4fc';
  }
};

// --- Main Component ---
export default function App() {
  const [activeFilter, setActiveFilter] = useState('all');

  const filteredPosts = postsData.filter(post =>
    activeFilter === 'all' ? true : post.type === activeFilter
  );

  const navigate = useNavigate();

  const renderMedia = (post) => {
    if (post.mediaType === 'video') {
      return (
        <video controls preload="metadata" poster={post.posterSrc}>
          <source src={post.mediaSrc} type="video/mp4" />
        </video>
      );
    }
    return <img src={post.mediaSrc} alt={post.title} loading="lazy" />;
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles styles={{ body: { scrollBehavior: 'smooth' } }} />

      {/* Header */}
      {/* <StyledAppBar>
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2, flexWrap: 'wrap', gap: 2 }}>
            <Box onClick={() => navigate(-1)}>
              <GradientLogo component="h1">JMK</GradientLogo>
              <Typography variant="caption" sx={{ color: '#9ca3af', display: 'block', mt: 0.5 }}>
                premium lens · motion · narrative
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 4 }}>
              {['Journal', 'Visuals', 'Cinema', 'Dispatch'].map(item => (
                <Link key={item} href="#" underline="none" sx={{ color: '#d1d5db', fontWeight: 500, '&:hover': { color: '#fff' } }}>
                  {item}
                </Link>
              ))}
            </Box>
          </Box>
        </Container>
      </StyledAppBar> */}

      <main>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          {/* Hero */}
          {/* <Box sx={{ textAlign: 'center', my: { xs: 4, md: 6 } }}>
            <Chip
              label="✦ EDITION 2026 ✦"
              sx={{
                bgcolor: alpha('#6366f1', 0.2),
                color: '#a5b4fc',
                borderRadius: 100,
                border: '0.5px solid rgba(165, 180, 252, 0.3)',
                mb: 3,
                backdropFilter: 'blur(4px)',
                fontSize: '0.75rem',
              }}
            />
            <HeroGradient variant="h2">
              image · video · news<br />in perfect resonance
            </HeroGradient>
            <Typography variant="body1" sx={{ color: '#9ca3af', maxWidth: 600, mx: 'auto', mt: 2, fontSize: '1.1rem' }}>
              immersive storytelling with cinematic edge & editorial depth
            </Typography>
          </Box> */}

          {/* Filter Chips */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, flexWrap: 'wrap', my: 4 }}>
            <FilterChip
              key="home"
              label="Home"
              onClick={() => navigate(-1)}
            />
            {filterOptions.map(option => (
              <FilterChip
                key={option.value}
                label={option.label}
                active={activeFilter === option.value}
                onClick={() => setActiveFilter(option.value)}
              />
            ))}
          </Box>
        </Container>

        {/* Featured Section */}
        <Container maxWidth="xl">
          <FeaturedWrapper elevation={0}>
            <Grid item xs={6} md={3}>
              <Box sx={{ aspectRatio: { xs: '2/5', md: '9/6' }, overflow: 'hidden' }}>
                <img
                  src={home}
                  alt="featured editorial"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: { xs: 3, sm: 4, md: 5 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Typography
                  variant="overline"
                  sx={{
                    color: "#facc15",
                    letterSpacing: 2,
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  ✦ SMART SCHOOL MANAGEMENT
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    lineHeight: 1.2,
                    mb: 2,
                    fontSize: { xs: "1.8rem", md: "2.2rem" },
                  }}
                >
                  Simplifying School Administration
                </Typography>

                <Typography
                  sx={{
                    color: "#b9c3d4",
                    mb: 3,
                  }}
                >
                  Manage admissions, attendance, examinations, fees, transport,
                  library, staff, and parent communication—all from one secure,
                  cloud-based platform.
                </Typography>

                <Link
                  href="/features"
                  underline="hover"
                  sx={{
                    color: "#a78bfa",
                    fontWeight: 600,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  Explore Features →
                </Link>
              </Box>
            </Grid>
          </FeaturedWrapper>
        </Container>

        {/* Blog Grid */}
        <Container maxWidth="xl" //sx={{ px: { xs: 2, sm: 3, md: 4 } }}
        >
          <Box
            display="grid"
            gridTemplateColumns={{
              xs: "1fr",        // mobile → 1 column
              sm: "1fr 1fr",    // tablet → 2 columns
              md: "1fr 1fr"     // desktop → 2 columns
            }}
            gap={4}
          >
            {filteredPosts.map(post => (
              <Box key={post.id}>
                <PremiumCard>
                  <MediaWrapper>
                    {renderMedia(post)}
                  </MediaWrapper>
                  <CardContent sx={{ p: 3 }}>
                    <Chip
                      label={post.metaCategory}
                      size="small"
                      sx={{
                        bgcolor: alpha(getMetaBadgeColor(post.type), 0.2),
                        color: getMetaBadgeColor(post.type),
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                        mb: 2,
                        borderRadius: 10,
                      }}
                    />
                    <Typography variant="h5" component="h3" sx={{ fontWeight: 700, mb: 1.5, lineHeight: 1.3, fontSize: '1.55rem' }}>
                      {post.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#b9c3d4', mb: 3, opacity: 0.85 }}>
                      {post.excerpt}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(255,255,255,0.08)', pt: 2 }}>
                      <Typography variant="caption" sx={{ color: '#8b92a8' }}>
                        {post.readTime || post.duration}
                      </Typography>
                      <Link href="#" underline="hover" sx={{ color: '#c7d2fe', fontWeight: 600, fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: 1, '&:hover': { gap: 2, color: '#fff' } }}>
                        {post.linkText}
                        <ArrowForwardIcon sx={{ fontSize: 14 }} />
                      </Link>
                    </Box>
                  </CardContent>
                </PremiumCard>
              </Box>
            ))}
          </Box>
        </Container>

        {/* Newsletter */}
        <Container maxWidth="xl">
          <NewsletterBox>
            <Typography
              variant="h4"
              sx={{ fontWeight: 600, fontSize: { xs: "1.8rem", md: "2rem" } }}
            >
              Stay Updated with JMK School ERP
            </Typography>

            <Typography sx={{ mt: 1.5, color: "#a0a9c2" }}>
              Subscribe to receive product updates, new features, school management tips, and important announcements.
            </Typography>
            <Box component="form" sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap', mt: 3 }}>
              <TextField
                placeholder="Enter your email address"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#11141f',
                    borderRadius: 60,
                    '& fieldset': { borderColor: '#2d3348' },
                  },
                  width: { xs: '100%', sm: 280 },
                }}
              />
              <Button
                variant="contained"
                sx={{
                  bgcolor: '#01036400',
                  borderRadius: 60,
                  border: '1px solid dimgrey',
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  '&:hover': { bgcolor: '#090c15', border: '1px solid white', transform: 'scale(0.98)' },
                }}
              >
                Subscribe free
              </Button>
            </Box>
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 2.5, fontSize: "0.7rem" }}
            >
              We respect your privacy. No spam, only important updates.
            </Typography>
          </NewsletterBox>
        </Container>

        {/* Footer */}
        {/* <Box component="footer" sx={{ borderTop: '1px solid rgba(255,255,255,0.05)', py: 5, textAlign: 'center' }}>
          <Container maxWidth="xl">
            <Typography variant="body2" sx={{ color: '#7e85a3' }}>
              © 2026 JMK — where image, video & news converge into premium narrative.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap', mt: 3 }}>
              {['about the journal', 'submit work', 'subscribe to premium'].map(link => (
                <Link key={link} href="#" underline="hover" sx={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                  {link}
                </Link>
              ))}
            </Box>
          </Container>
        </Box> */}
      </main>
    </ThemeProvider>
  );
}